import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, publicFileUrl } from "@/lib/api";
import { formatRp, formatDateID, formatDateTimeID, periodStatusLabel, sourceLabel, categoryLabel } from "@/lib/format";
import Header from "@/components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PaymentSection from "@/components/PaymentSection";
import { BookOpen, Sprout, Target, TrendingUp, Wallet, Clock, Info, FileText, Share2, BarChart3, HeartHandshake } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

export default function PublicPage() {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tx, setTx] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [progress, setProgress] = useState([]);
  const [classes, setClasses] = useState([]);
  const [chart, setChart] = useState({ series: [] });
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [s, se, n, p, c, ch] = await Promise.all([
          api.get("/public/summary"),
          api.get("/public/settings"),
          api.get("/public/needs"),
          api.get("/public/progress"),
          api.get("/public/classes"),
          api.get("/public/daily-chart"),
        ]);
        setSummary(s.data);
        setSettings(se.data);
        setNeeds(n.data);
        setProgress(p.data);
        setClasses(c.data);
        setChart(ch.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const loadTx = async () => {
    const params = {};
    if (filterStart) params.start = filterStart;
    if (filterEnd) params.end = filterEnd;
    if (filterType !== "all") params.type = filterType;
    const { data } = await api.get("/public/transactions", { params });
    setTx(data);
  };
  useEffect(() => { loadTx(); /* eslint-disable-next-line */ }, [filterType]);

  if (!summary) {
    return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Memuat...</div>;
  }

  const pct = Math.min(summary.progress_pct || 0, 100);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header logoFileId={summary.logo_file_id} siteName={summary.name} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Hero */}
        <section className="mb-8" data-testid="public-hero">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <Badge className="bg-[#FBF5E8] text-[#C59B27] border-[#C59B27]/30 mb-2" data-testid="public-status-badge">
                {periodStatusLabel[summary.period.status]}
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1B4D3E]">{summary.name}</h1>
              <p className="text-base sm:text-lg text-[#64748B] mt-2 max-w-2xl">{summary.tagline}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Hari Sekolah</div>
              <div className="text-3xl font-bold text-[#1B4D3E]" data-testid="public-current-day">
                {summary.period.current_day}/{summary.period.total_days}
              </div>
              {summary.period.start_date && (
                <div className="text-xs text-[#64748B] mt-1">
                  {formatDateID(summary.period.start_date)} – {formatDateID(summary.period.end_date)}
                </div>
              )}
            </div>
          </div>

          {/* Progress card */}
          <Card className="p-5 sm:p-6 lg:p-8 border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold">
                <TrendingUp size={18} /> Progres Penghimpunan
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-[#1B4D3E]" data-testid="public-progress-pct">{pct.toFixed(1)}%</div>
            </div>
            <div className="progress-track mb-4"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat icon={<Target size={16} />} label="Target" value={formatRp(summary.target_amount)} testid="public-target" />
              <Stat icon={<TrendingUp size={16} />} label="Penerimaan" value={formatRp(summary.total_received)} testid="public-total-received" color="text-emerald-700" />
              <Stat icon={<FileText size={16} />} label="Pengeluaran" value={formatRp(summary.expenses)} testid="public-expenses" color="text-red-700" />
              <Stat icon={<Wallet size={16} />} label="Saldo" value={formatRp(summary.balance)} testid="public-balance" color="text-[#C59B27]" />
            </div>
            {summary.updated_at && (
              <div className="mt-4 text-xs text-[#94A3B8] flex items-center gap-1" data-testid="public-updated-at">
                <Clock size={12} /> Pembaruan terakhir: {formatDateTimeID(summary.updated_at)}
              </div>
            )}
          </Card>

          <div className="mt-4 p-4 rounded-lg bg-[#EAF2EF] border border-[#1B4D3E]/10 text-sm text-[#1B4D3E] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="public-voluntary-notice">
            <span>Infak sukarela, sesuai kelapangan keluarga. Semua anak mendapatkan manfaat yang sama.</span>
            <ShareWhatsAppButton summary={summary} />
          </div>
        </section>

        {/* Daily chart */}
        {chart.series && chart.series.length > 0 && (
          <Card className="p-5 mb-6 border-[#E2E8F0]" data-testid="public-chart-card">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-[#1B4D3E]" />
              <h3 className="text-lg font-semibold text-[#1B4D3E]">Rekap Harian Penerimaan</h3>
            </div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={chart.series} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)} />
                  <Tooltip
                    cursor={{ fill: "#EAF2EF" }}
                    formatter={(v) => formatRp(v)}
                    labelFormatter={(label, items) => {
                      const d = items?.[0]?.payload?.date;
                      return d ? `Hari ${label} — ${formatDateID(d)}` : `Hari ${label}`;
                    }}
                    contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8 }}
                  />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {chart.series.map((entry, idx) => (
                      <Cell key={idx} fill={entry.is_today ? "#C59B27" : entry.is_past ? "#1B4D3E" : "#CBD5E1"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#64748B]">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#1B4D3E]" /> Hari lampau</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#C59B27]" /> Hari ini</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#CBD5E1]" /> Hari mendatang</span>
            </div>
          </Card>
        )}

        {/* Alumni CTA */}
        <Card className="p-5 mb-6 bg-[#FBF5E8] border-[#C59B27]/30" data-testid="alumni-cta-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold mb-1">
                <HeartHandshake size={18} /> Alumni & Donatur
              </div>
              <p className="text-sm text-[#64748B]">Bagikan tautan khusus donatur alumni untuk kontribusi mudah dan transparan.</p>
            </div>
            <Link to="/infaq/alumni" data-testid="alumni-link-btn">
              <Button className="bg-[#1B4D3E] hover:bg-[#143E32] text-white">Buka Halaman Alumni →</Button>
            </Link>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="laporan" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto bg-white border border-[#E2E8F0]" data-testid="public-tabs">
            <TabsTrigger value="laporan" data-testid="tab-laporan">Laporan Dana</TabsTrigger>
            <TabsTrigger value="kebutuhan" data-testid="tab-kebutuhan">Kebutuhan</TabsTrigger>
            <TabsTrigger value="perkembangan" data-testid="tab-perkembangan">Perkembangan</TabsTrigger>
            <TabsTrigger value="tentang" data-testid="tab-tentang">Tentang</TabsTrigger>
          </TabsList>

          <TabsContent value="laporan" className="mt-6">
            <Card className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-end">
                <div className="flex-1">
                  <label className="text-xs text-[#64748B] block mb-1">Dari tanggal</label>
                  <Input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} data-testid="filter-start-date" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-[#64748B] block mb-1">Sampai tanggal</label>
                  <Input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} data-testid="filter-end-date" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-[#64748B] block mb-1">Jenis</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger data-testid="filter-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua</SelectItem>
                      <SelectItem value="receipt">Penerimaan</SelectItem>
                      <SelectItem value="expense">Pengeluaran</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadTx} className="bg-[#1B4D3E] hover:bg-[#143E32]" data-testid="apply-filter-btn">Terapkan</Button>
              </div>

              {/* Transactions table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="public-tx-table">
                  <thead>
                    <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                      <th className="py-2 pr-4">Tanggal</th>
                      <th className="py-2 pr-4">Keterangan</th>
                      <th className="py-2 pr-4 text-right">Nominal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tx.length === 0 && (
                      <tr><td colSpan={3} className="py-8 text-center text-[#94A3B8]">Belum ada transaksi.</td></tr>
                    )}
                    {tx.map((t) => (
                      <tr key={t.type + t.id} className="border-b border-[#F1F5F9]">
                        <td className="py-3 pr-4 whitespace-nowrap">{formatDateID(t.date)}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <Badge className={t.type === "receipt" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                              {t.type === "receipt" ? "Masuk" : "Keluar"}
                            </Badge>
                            <span>{t.label}</span>
                          </div>
                        </td>
                        <td className={`py-3 pr-4 text-right font-semibold ${t.type === "receipt" ? "text-emerald-700" : "text-red-700"}`}>
                          {t.type === "receipt" ? "+" : "−"}{formatRp(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Classes summary (no ranking - alphabetical) */}
              {classes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#1B4D3E] mb-3">Rekap per Kelas (terkonfirmasi)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="public-classes-grid">
                    {classes.map((c) => (
                      <div key={c.id} className="p-3 rounded-lg bg-[#FBF5E8] border border-[#C59B27]/20">
                        <div className="text-xs text-[#64748B]">Kelas</div>
                        <div className="font-semibold text-[#1B4D3E]">{c.name}</div>
                        <div className="text-sm text-[#C59B27] font-semibold mt-1">{formatRp(c.total)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="kebutuhan" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-needs-grid">
              {needs.map((n) => (
                <Card key={n.id} className="p-5 border-[#E2E8F0]">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {n.category === "kebun" ? <Sprout size={18} className="text-emerald-700" /> : <BookOpen size={18} className="text-[#1B4D3E]" />}
                      <h4 className="font-semibold text-[#1E293B]">{n.name}</h4>
                    </div>
                    <Badge className="bg-[#EAF2EF] text-[#1B4D3E] text-xs">{categoryLabel[n.category] || n.category}</Badge>
                  </div>
                  <div className="text-sm text-[#64748B] mb-3">{n.quantity} {n.unit}</div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#64748B]">Anggaran</span>
                    <span className="font-semibold">{formatRp(n.budget)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#64748B]">Realisasi</span>
                    <span className="font-semibold text-[#C59B27]">{formatRp(n.realized || 0)}</span>
                  </div>
                  <div className="progress-track"><div className="progress-fill" style={{ width: `${Math.min(((n.realized || 0) / n.budget) * 100, 100)}%` }} /></div>
                  <div className="mt-3">
                    <Badge className="bg-slate-100 text-slate-700 text-xs">Status: {n.status}</Badge>
                  </div>
                  {Array.isArray(n.detail) && n.detail.length > 0 && (
                    <ul className="mt-3 text-xs text-[#64748B] space-y-1 list-disc list-inside">
                      {n.detail.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="perkembangan" className="mt-6">
            {progress.length === 0 ? (
              <Card className="p-8 text-center text-[#94A3B8]" data-testid="public-progress-empty">Belum ada perkembangan dipublikasikan.</Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="public-progress-grid">
                {progress.map((p) => (
                  <Card key={p.id} className="overflow-hidden border-[#E2E8F0]">
                    {p.photo_file_ids?.[0] && (
                      <img src={publicFileUrl(p.photo_file_ids[0])} alt={p.title} className="w-full h-56 object-cover" />
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-[#EAF2EF] text-[#1B4D3E]">{categoryLabel[p.category]}</Badge>
                        {p.is_plan && <Badge className="bg-amber-100 text-amber-800">Rencana penataan</Badge>}
                        <span className="text-xs text-[#94A3B8]">{formatDateID(p.date)}</span>
                      </div>
                      <h3 className="font-semibold text-lg text-[#1E293B] mb-1">{p.title}</h3>
                      <p className="text-sm text-[#64748B]">{p.description}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tentang" className="mt-6">
            <Card className="p-6 border-[#E2E8F0]">
              <h2 className="text-2xl font-bold text-[#1B4D3E] mb-3">Tentang Gerakan</h2>
              <p className="text-[#64748B] mb-4 leading-relaxed">
                {summary.tagline} Gerakan ini berlangsung selama {summary.period.total_days} hari sekolah (Senin–Jumat).
                Rp2.000 per hari adalah ajakan — <strong>bukan kewajiban atau batas minimal</strong>.
                Pemberian boleh harian, mingguan, atau sesekali, sesuai kelapangan keluarga.
              </p>

              <PaymentSection settings={settings} />

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="p-4 rounded-lg bg-[#EAF2EF] border border-[#1B4D3E]/10">
                  <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold mb-2"><Info size={16} /> Penanggung Jawab</div>
                  <p className="text-sm text-[#64748B] whitespace-pre-wrap">{settings?.pj_info || "Informasi akan ditambahkan oleh pengurus."}</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FBF5E8] border border-[#C59B27]/20">
                  <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold mb-2"><Info size={16} /> Catatan Tambahan</div>
                  <p className="text-sm text-[#64748B] whitespace-pre-wrap">{settings?.how_to_contribute || "Informasi akan ditambahkan oleh pengurus."}</p>
                </div>
              </div>
              <div className="mt-6 text-xs text-[#94A3B8]">
                Transparansi: tidak ada peringkat donatur, tidak ada tunggakan, tidak ada daftar nama siswa.
              </div>
              <div className="mt-6 text-sm">
                <Link to="/infaq/login" className="text-[#1B4D3E] underline" data-testid="link-login-page">Masuk pengurus →</Link>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="border-t border-[#E2E8F0] bg-white mt-16">
        <div className="max-w-7xl mx-auto px-4 py-6 text-xs text-[#94A3B8] text-center">
          © {new Date().getFullYear()} {summary.name}. Transparansi untuk kepercayaan.
        </div>
      </footer>
    </div>
  );
}

function ShareWhatsAppButton({ summary }) {
  const share = () => {
    const url = `${window.location.origin}/infaq`;
    const pct = (summary.progress_pct || 0).toFixed(1);
    const lines = [
      `*${summary.name}*`,
      summary.tagline ? `_${summary.tagline}_` : "",
      "",
      `📊 *Ringkasan Mingguan*`,
      `• Target: ${formatRp(summary.target_amount)}`,
      `• Terkumpul: ${formatRp(summary.total_received)} (${pct}%)`,
      `• Pengeluaran: ${formatRp(summary.expenses)}`,
      `• Saldo: ${formatRp(summary.balance)}`,
      summary.period?.current_day
        ? `• Hari sekolah ke-${summary.period.current_day} dari ${summary.period.total_days}`
        : "",
      "",
      "Infak sukarela, sesuai kelapangan keluarga. Semua anak mendapatkan manfaat yang sama.",
      "",
      `Laporan lengkap & transparan: ${url}`,
    ].filter(Boolean).join("\n");
    const waUrl = `https://wa.me/?text=${encodeURIComponent(lines)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <Button
      onClick={share}
      size="sm"
      className="bg-[#25D366] hover:bg-[#1EBE57] text-white shrink-0"
      data-testid="share-whatsapp-btn"
    >
      <Share2 size={14} className="mr-2" /> Bagikan ke WhatsApp
    </Button>
  );
}

function Stat({ icon, label, value, color = "text-[#1E293B]", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs text-[#64748B] uppercase tracking-wider flex items-center gap-1">{icon} {label}</div>
      <div className={`text-lg sm:text-xl font-bold ${color} mt-1`}>{value}</div>
    </div>
  );
}
